import { FaCertificate, FaDownload, FaExternalLinkAlt, FaFileContract, FaFileSignature } from 'react-icons/fa'
import { Banner, Button, Card, EmptyState, KeyValueList, Spinner, StatusPill } from '../../../../../ui'
import { SignatureEvidence, formatDateTime } from '../shared'
import s from './styles.module.scss'

export type SignatureEvidenceStatus = 'loading' | 'ready' | 'none' | 'error'

export interface SignatureEvidenceState {
    status: SignatureEvidenceStatus
    evidence: SignatureEvidence | null
    error?: string
}

export interface SignatureEvidenceCardProps {
    clientName: string
    state: SignatureEvidenceState
    onRetry: () => void
}

const describeLocation = (location: SignatureEvidence['location']) => {
    if (!location) return null
    const parts = [location.city, location.region, location.country].filter(Boolean)
    return parts.length ? parts.join(', ') : null
}

const describeCoordinates = (location: SignatureEvidence['location']) => {
    if (!location || location.latitude == null || location.longitude == null) return null
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
}

/**
 * Audit trail for the client's signed CFA: the drawn signature alongside
 * when, where and on what it was signed, with links to the supporting
 * documents on S3. Rendered only when the client record says the CFA is signed.
 */
export const SignatureEvidenceCard = ({ clientName, state, onRetry }: SignatureEvidenceCardProps) => {
    const { status, evidence } = state

    const headerPill =
        status === 'ready' ? (
            <StatusPill tone="good" dot>
                Recorded
            </StatusPill>
        ) : status === 'loading' ? (
            <StatusPill>Loading…</StatusPill>
        ) : status === 'none' ? (
            <StatusPill tone="warn" dot>
                No record
            </StatusPill>
        ) : (
            <StatusPill tone="bad" dot>
                Unavailable
            </StatusPill>
        )

    const documents = evidence?.documents
    const hasDocuments = !!(documents?.esign_certificate || documents?.signed_cfa || documents?.signature_png)

    const footer =
        status === 'ready' && hasDocuments ? (
            <div className={s.cardActions}>
                {documents?.esign_certificate && (
                    <Button
                        as="a"
                        size="sm"
                        href={documents.esign_certificate}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<FaCertificate />}
                    >
                        E-sign certificate
                    </Button>
                )}
                {documents?.signed_cfa && (
                    <Button
                        as="a"
                        size="sm"
                        href={documents.signed_cfa}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<FaFileContract />}
                    >
                        Signed CFA
                    </Button>
                )}
                {documents?.signature_png && (
                    <Button
                        as="a"
                        size="sm"
                        href={documents.signature_png}
                        target="_blank"
                        rel="noopener noreferrer"
                        icon={<FaDownload />}
                    >
                        Signature image
                    </Button>
                )}
            </div>
        ) : undefined

    return (
        <Card
            title="Signature evidence"
            subtitle="Audit trail captured when the CFA was signed"
            actions={headerPill}
            footer={footer}
        >
            {status === 'loading' && <Spinner text="Loading signature record…" />}

            {status === 'error' && (
                <EmptyState
                    tone="error"
                    title="Could not load the signature record"
                    description={state.error || 'The signature service did not respond.'}
                    action={
                        <Button size="sm" onClick={onRetry}>
                            Retry
                        </Button>
                    }
                />
            )}

            {status === 'none' && (
                <Banner tone="warn" title="No signature record found">
                    The client record is marked as signed, but no signature_links row with a signature could be found in
                    the {evidence?.origin ?? 'introducer'} database. The signed CFA document may still be available from
                    the CFA &amp; signing panel.
                </Banner>
            )}

            {status === 'ready' && evidence && (
                <div className={s.evidenceLayout}>
                    <figure className={s.signatureFrame}>
                        {evidence.signature_image ? (
                            <img
                                className={s.signatureImage}
                                src={evidence.signature_image.src}
                                alt={`Signature drawn by ${clientName}`}
                            />
                        ) : (
                            <div className={s.signatureMissing}>
                                <FaFileSignature aria-hidden="true" />
                                <span>No signature image stored</span>
                            </div>
                        )}
                        <figcaption className={s.signatureCaption}>
                            <span className={s.signatureSigner}>{clientName}</span>
                            <span>{formatDateTime(evidence.signature_date)}</span>
                        </figcaption>
                    </figure>

                    <div className={s.stackSm}>
                        <KeyValueList
                            columns
                            items={[
                                { label: 'Signed at', value: formatDateTime(evidence.signature_date) },
                                ...(evidence.original_signature_date &&
                                evidence.original_signature_date !== evidence.signature_date
                                    ? [
                                          {
                                              label: 'Original signature',
                                              value: formatDateTime(evidence.original_signature_date),
                                          },
                                      ]
                                    : []),
                                { label: 'IP address', value: evidence.ip_address, mono: true },
                                {
                                    label: 'Approx. location',
                                    value: describeLocation(evidence.location),
                                },
                                { label: 'Postcode area', value: evidence.location?.postcode ?? null },
                                { label: 'Coordinates', value: describeCoordinates(evidence.location), mono: true },
                                { label: 'Network / ISP', value: evidence.location?.isp ?? null },
                                { label: 'Device', value: evidence.device },
                                { label: 'Browser', value: evidence.browser },
                                ...(evidence.user_agent
                                    ? [{ label: 'User agent', value: evidence.user_agent, mono: true }]
                                    : []),
                                { label: 'Case ref', value: evidence.case_ref, mono: true },
                                { label: 'Signing token', value: evidence.token, mono: true },
                                { label: 'Link created', value: formatDateTime(evidence.link_created_at) },
                                { label: 'Link opened', value: evidence.link_opened ? 'Yes' : 'No' },
                                { label: 'Sent by', value: evidence.agent },
                                { label: 'Record status', value: evidence.status },
                                ...(evidence.signing_url
                                    ? [
                                          {
                                              label: 'Signing page',
                                              value: (
                                                  <a
                                                      href={evidence.signing_url}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className={s.link}
                                                  >
                                                      Open <FaExternalLinkAlt aria-hidden="true" />
                                                  </a>
                                              ),
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                        <p className={s.footnote}>
                            Location, coordinates and network are estimated from the recorded IP address at the time of
                            viewing; the signing page does not capture GPS. Mobile and VPN connections can place the
                            client some distance from where they actually signed.
                            {evidence.other_signed_rows > 0 &&
                                ` ${evidence.other_signed_rows} further signed record${
                                    evidence.other_signed_rows === 1 ? '' : 's'
                                } exist for this client; the most relevant is shown.`}
                        </p>
                    </div>
                </div>
            )}
        </Card>
    )
}
